import * as React from 'react';
import { useCallback, useMemo } from 'react';
import ButtonBase from '@mui/material/ButtonBase';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Props } from '../CheckoutAdditionalService';
import { useTheme } from '../../../theme';
import { CdnIcon, CdnIconNames } from '@shared/ui';
import Money from '../../../Money';
import Button from '../../../BaseComponents/Button';
import { SafeHtml } from '@shared/ui';

const AggregatorService: React.FC<Props> = ({
	className,
	coverClassName,
	descriptionClassName,
	coverBackgroundColor,
	coverBackgroundUrl,
	aggregationIcons,
	header,
	description,
	children,
	priceFrom,
	priceTotal,
	discount,
	buttons,
	displayOrder,
	hideFooterButtons,
	isSelected,
	isLoading,
	addClick,
	onDelete,
	renderHeaderPrice
}) => {
	const theme = useTheme('CheckoutAdditionalService').AggregatorService;
	const { t } = useTranslation('CheckoutAdditionalService');

	const hasTotalPrice = priceTotal?.amount > 0;
	const hasFromPrice = priceFrom?.amount >= 0;
	const hasPrice = hasTotalPrice || hasFromPrice;

	const filteredButtons = useMemo(() => {
		if (!buttons || !isSelected) {
			return [];
		}

		if (isLoading) {
			return [buttons[0]];
		}

		return buttons;
	}, [buttons, isSelected, isLoading]);

	const onDescriptionClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
		if ((e.target as HTMLElement)?.tagName === 'A') {
			e.stopPropagation();
		}
	};

	const onAddHandler = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();

			addClick();
		},
		[addClick]
	);

	return (
		<div
			className={cn(theme.container, className, {
				[theme.container_active]: !isSelected
			})}
			onClick={!isSelected ? addClick : () => {}}
			style={{ order: displayOrder }}
		>
			<div className={cn(theme.cover, coverClassName)} style={{ background: coverBackgroundColor }}>
				{coverBackgroundUrl && <img className={theme.image} src={coverBackgroundUrl} />}

				{!isSelected && (
					<ButtonBase onClick={onAddHandler} className={theme.add} aria-hidden="false">
						<CdnIcon icon="Action/Add" />
					</ButtonBase>
				)}

				{isSelected && <div className={cn(theme.checked, { [theme.checked_removable]: !!onDelete })} />}

				{aggregationIcons && (
					<div className={theme.icons}>
						{aggregationIcons.map((icon, key) =>
							typeof icon === 'string' ? (
								<CdnIcon key={`${icon}-${key}`} icon={icon as CdnIconNames} />
							) : (
								<React.Fragment key={key}>{icon}</React.Fragment>
							)
						)}
					</div>
				)}
			</div>

			<div className={theme.content}>
				<div className={theme.header}>{header}</div>

				{isSelected ? (
					<div className={theme.content__services}>{children}</div>
				) : (
					<div className={cn(theme.description, descriptionClassName)}>
						{discount && <div className={theme.discount}>{discount}</div>}

						{React.isValidElement<React.HTMLAttributes<HTMLDivElement>>(description) &&
							React.cloneElement(description, { onClick: onDescriptionClickHandler })}

						{!React.isValidElement(description) && (
							<SafeHtml tag="div" onClick={onDescriptionClickHandler} html={description as string} />
						)}
					</div>
				)}

				<div
					className={cn(theme.footer, {
						[theme.footer_multiButtons]: isSelected && filteredButtons?.length > 1,
						[theme.footer_hiddenButtons]: hideFooterButtons
					})}
				>
					{!hideFooterButtons && (
						<div className={theme.buttons}>
							{filteredButtons.length > 0 ? (
								filteredButtons.map((button, index) => (
									<Button
										key={index}
										variant="optionEdit"
										className={theme.button}
										isLoading={isLoading || button.isLoading}
										onClick={button.action}
									>
										{button.text}
									</Button>
								))
							) : (
								<Button
									variant="optionAction"
									className={cn(theme.button, theme.button_add)}
									isLoading={isLoading}
									onClick={onAddHandler}
								>
									{t('Add')}
								</Button>
							)}
						</div>
					)}

					{hasPrice && (
						<div className={theme.totalPrice}>
							{isSelected
								? renderHeaderPrice?.() ??
								  (hasTotalPrice && (
										<>
											{t('Total')}: <Money money={priceTotal} />
										</>
								  ))
								: hasFromPrice && (
										<>
											{t('from')} <Money money={priceFrom} />
										</>
								  )}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default AggregatorService;
